import type {
  ActivePcosResponse,
  AdminUserResponse,
  AdminUsersListResponse,
  ApiError,
  AuthConfigResponse,
  CreateUserRequest,
  CreateUserResponse,
  DeactivateUserResponse,
  InviteUserResponse,
  PatchUserRequest,
  RevokeUserSessionsResponse,
  UnlockUserResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginRequest,
  LoginResponse,
  SetPasswordRequest,
  SetPasswordResponse,
  ApplyEmployeeDefaultsRequest,
  ApplyEmployeeDefaultsResponse,
  ApplyRatesToSavedRequest,
  ApplyRatesToSavedResponse,
  AssignBlocksResponse,
  ActivityCodeIgnoreRequest,
  ActivityCodeIgnoreResponse,
  CodeRemapSuccessResponse,
  CopySourcesResponse,
  CreateFeedbackNoteRequest,
  CreateFeedbackNoteResponse,
  CreateProjectRequest,
  CreateProjectResponse,
  DismissEquipmentReviewNoteResponse,
  FeedbackNotePatchRequest,
  FeedbackNoteResponse,
  FeedbackNotesListResponse,
  FeedbackNoteStatus,
  EmployeeWriteRequest,
  EquipmentWriteRequest,
  FundingTranche,
  FundingTrancheWriteRequest,
  InboxBlockReplaceRequest,
  InboxBlockReplaceResponse,
  InboxBlockResponse,
  InboxLaborReassignResponse,
  InboxBlockSaveRequest,
  InboxLaborAddRequest,
  InboxEquipmentAddRequest,
  InboxLaborReassignRequest,
  InboxEquipmentReassignRequest,
  InboxMaterialAddRequest,
  InboxListResponse,
  InboxPdfDetailResponse,
  InboxVerifyAckResponse,
  IntakeHealthResponse,
  ReparsePdfResponse,
  ForemanCoverageResponse,
  ForemanCoverageMutationResponse,
  PauseForemanCoverageRequest,
  ResumeForemanCoverageRequest,
  MaterialQueueConfirmRequest,
  MaterialQueueItemResponse,
  MaterialQueueListResponse,
  MaterialWriteRequest,
  MeResponse,
  PcoAttentionResponse,
  PcoCumulativeResponse,
  PcoGeneratedDocumentsResponse,
  PcoStatus,
  PcoStatusPatchRequest,
  PcoWriteRequest,
  ManualAddResponse,
  ManualEquipmentAddRequest,
  ManualLaborAddRequest,
  ManualMaterialAddRequest,
  SubcontractorLineAddRequest,
  SubcontractorLinePatchRequest,
  SubcontractorLineResponse,
  ProjectPatchRequest,
  ProjectPatchResponse,
  ProjectTotals,
  MoveBlockRequest,
  MoveBlockResponse,
  TransferBlockProjectRequest,
  TransferBlockProjectResponse,
  MoveRollupSubmissionRequest,
  MoveRollupSubmissionResponse,
  RateComparisonKind,
  RateComparisonResponse,
  RequestMagicLinkResponse,
  RollupBlocksResponse,
  RollupLineEditRequest,
  RollupLineEditResponse,
  RollupLineRowsResponse,
  RollupDetail,
  RollupPreviewResponse,
  CloseWithoutSendRequest,
  EditImportedTotalRequest,
  EditImportedTotalResponse,
  CloseWithoutSendResponse,
  ReopenRollupRequest,
  ReopenRollupResponse,
  ApproveRollupResponse,
  RollupListResponse,
  SendContactsResponse,
  SignedPdfUrlResponse,
  SubmissionsListResponse,
  PcoSubmissionsResponse,
  ProjectSubmissionsResponse,
  SubmissionResponse,
  StartNextSubmissionRequest,
  AddHistoricalSubmissionRequest,
  CloseAndStartNextRequest,
  CloseAndStartNextResponse,
  DeleteSubmissionResponse,
  OpenSubmissionRequest,
  SubmissionPatchRequest,
  SubmitRollupRequest,
  SubmitRollupResponse,
  SettingsActivityCode,
  SettingsEmployee,
  SettingsEquipment,
  SettingsMaterial,
  SettingsPco,
  SettingsProject,
  SettingsForemanPlaceholder,
  SettingsUnknownsResponse,
  VarianceCreateRequest,
  VarianceItemResponse,
  VarianceListResponse,
  VariancePatchRequest,
  VarianceStatus,
  VerifyMagicLinkResponse,
} from '@coffman-tm/shared';
import {
  ACTIVE_PROJECT_HEADER,
  activeProjectHeader,
  handleProjectForbidden,
} from './activeProject';

export type VarianceStatusFilter = VarianceStatus | 'all';

export type MaterialQueueStatusFilter = 'pending' | 'confirmed' | 'skipped' | 'edited' | 'all';

const BASE_URL = import.meta.env.VITE_API_BASE_URL.replace(/\/$/, '');

/**
 * What went wrong, beyond the HTTP status. Before this existed every failure
 * was an HTTP failure — a request that never came back simply never rejected,
 * which is how a hung fetch turned into a permanently "loading" page.
 *
 *   'http'    — the server answered with a non-2xx. `status` is meaningful.
 *   'timeout' — we gave up waiting. `status` is 0; nothing reached the user.
 *   'network' — fetch itself rejected (offline, DNS, CORS). `status` is 0.
 *
 * Intentional aborts (navigation, unmount) are deliberately NOT in this union:
 * they never become an ApiCallError at all, because they aren't failures. The
 * original AbortError propagates so callers can drop it on the floor.
 */
export type ApiErrorKind = 'http' | 'timeout' | 'network';

export class ApiCallError extends Error {
  readonly kind: ApiErrorKind;

  constructor(
    public status: number,
    public payload: ApiError | { error?: string },
    kind: ApiErrorKind = 'http',
  ) {
    super(payload.error ?? `HTTP ${status}`);
    this.name = 'ApiCallError';
    this.kind = kind;
  }

  /**
   * `status` is 0 and `payload.error` stays a stable machine code so the
   * existing `err.payload.error` consumers keep working; `message` is
   * overwritten with prose because that's what the error banners render.
   */
  static timeout(timeoutMs: number): ApiCallError {
    const err = new ApiCallError(0, { error: 'timeout' }, 'timeout');
    err.message = `The server didn't respond within ${Math.round(
      timeoutMs / 1000,
    )}s. It may still be working — try again in a moment.`;
    return err;
  }

  static network(): ApiCallError {
    const err = new ApiCallError(0, { error: 'network_error' }, 'network');
    err.message = "Couldn't reach the server. Check your connection and try again.";
    return err;
  }
}

/**
 * Default ceiling for a normal JSON round trip. Long enough that a cold
 * Worker start or a slow D1 query still lands, short enough that Joey learns
 * something is wrong instead of staring at a spinner forever.
 */
export const REQUEST_TIMEOUT_MS = 20_000;

/**
 * xlsx renders and PDF packaging stream real work (exceljs over a full week of
 * entries), so they get a much longer leash than a plain read.
 */
export const DOWNLOAD_TIMEOUT_MS = 120_000;

/**
 * Reparse runs an entire PDF parse — including the upstream model call —
 * synchronously inside the request. It is by far the slowest endpoint.
 */
export const REPARSE_TIMEOUT_MS = 180_000;

/** Per-call transport overrides. Every api method that a query hook can reach
 *  accepts this as an optional trailing argument. */
export interface ApiCallOptions {
  /** Caller's cancellation signal — aborting is silent, never an error. */
  signal?: AbortSignal;
  /** Override the default timeout for this one call. */
  timeoutMs?: number;
}

/**
 * True for a cancellation, ours or the caller's. Name-based rather than
 * `instanceof DOMException` so it also holds in the Node test environment,
 * where DOMException may be absent.
 */
export function isAbortError(err: unknown): boolean {
  return typeof (err as { name?: unknown } | null)?.name === 'string'
    ? (err as { name: string }).name === 'AbortError'
    : false;
}

function isAbortLike(err: unknown): boolean {
  const name = (err as { name?: unknown } | null)?.name;
  return name === 'AbortError' || name === 'TimeoutError';
}

function timeoutReason(): unknown {
  if (typeof DOMException !== 'undefined') {
    return new DOMException('Request timed out', 'TimeoutError');
  }
  const err = new Error('Request timed out');
  err.name = 'TimeoutError';
  return err;
}

/**
 * Combine the caller's optional signal with a timeout into the one signal we
 * hand to fetch. `AbortSignal.any` does this natively; the fallback wires a
 * controller by hand for older engines. `release()` clears the fallback timer
 * so a fast response doesn't leave a pending abort behind.
 */
function withTimeoutSignal(
  callerSignal: AbortSignal | undefined,
  timeoutMs: number,
): { signal: AbortSignal; release: () => void } {
  const anyFn = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  if (typeof AbortSignal.timeout === 'function' && typeof anyFn === 'function') {
    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    return {
      signal: callerSignal
        ? anyFn.call(AbortSignal, [callerSignal, timeoutSignal])
        : timeoutSignal,
      release: () => {},
    };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(timeoutReason()), timeoutMs);
  const onCallerAbort = () => controller.abort(callerSignal?.reason);
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else callerSignal.addEventListener('abort', onCallerAbort);
  }
  return {
    signal: controller.signal,
    release: () => {
      clearTimeout(timer);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    },
  };
}

/**
 * Classify a rejected fetch. The caller-aborted check comes first and is based
 * on the signal, not the error name: some engines report every abort as a
 * generic AbortError regardless of reason, so the signal is the only reliable
 * witness to *whose* abort it was.
 */
function toRequestError(
  err: unknown,
  callerSignal: AbortSignal | undefined,
  timeoutMs: number,
): unknown {
  if (isAbortLike(err)) {
    // The caller cancelled (navigated away, unmounted). Propagate untouched —
    // this is an expected outcome, not a failure, and must stay silent.
    if (callerSignal?.aborted) return err;
    return ApiCallError.timeout(timeoutMs);
  }
  if (err instanceof ApiCallError) return err;
  return ApiCallError.network();
}

/**
 * Read a non-2xx body into an ApiError. Text first, then JSON.parse, rather
 * than `res.json()` in a try — the body can only be consumed once, so a failed
 * `res.json()` used to discard the evidence entirely. Non-JSON bodies (an HTML
 * error page, a proxy's plain-text complaint) are kept as `message` instead of
 * being dropped on the floor.
 */
async function readErrorPayload(res: Response): Promise<ApiError> {
  const fallback: ApiError = { error: `http_${res.status}` };
  const text = await res.text().catch(() => '');
  if (!text) return fallback;
  try {
    return JSON.parse(text) as ApiError;
  } catch {
    return { ...fallback, message: text.slice(0, 500) };
  }
}

interface RequestOptions extends ApiCallOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  token?: string | null;
  /**
   * Raw request body, sent as-is with `contentType` (no JSON.stringify). For
   * binary uploads — currently the subcontractor invoice PDF. Mutually
   * exclusive with `body`.
   */
  rawBody?: BodyInit;
  contentType?: string;
  /**
   * Read ANOTHER project this session belongs to, for this one call — the
   * cross-project transfer dialog listing the destination job's PCOs.
   *
   * It overrides the stored active-project header without touching the stored
   * pick, and it OPTS OUT of the stale-selection recovery: a 403 here is about
   * the id the dialog passed, not about the project the rest of the app is
   * standing in, so clearing the pick and reloading would throw the PM out of
   * an unrelated screen. The caller shows the error instead.
   */
  projectId?: string;
}

/**
 * Single place a non-2xx becomes an ApiCallError, so the stale-project
 * recovery can't be forgotten on one of the two transports.
 */
async function httpError(res: Response, opts: { skipProjectRecovery?: boolean } = {}): Promise<ApiCallError> {
  const err = new ApiCallError(res.status, await readErrorPayload(res));
  if (err.status === 403 && err.payload.error === 'project_forbidden' && !opts.skipProjectRecovery) {
    // The stored selection is no longer valid for this account. Drop it and
    // reload once against the server's default project.
    handleProjectForbidden();
  }
  return err;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...activeProjectHeader() };
  // Same key as the session's pick, so this REPLACES it. A differently-cased
  // literal would add a SECOND property that `Headers` folds into one
  // comma-joined value the server can only read as an unknown project id —
  // see ACTIVE_PROJECT_HEADER.
  if (opts.projectId) headers[ACTIVE_PROJECT_HEADER] = opts.projectId;
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.rawBody !== undefined && opts.contentType) headers['content-type'] = opts.contentType;
  if (opts.token) headers['authorization'] = `Bearer ${opts.token}`;

  const timeoutMs = opts.timeoutMs ?? REQUEST_TIMEOUT_MS;
  const { signal, release } = withTimeoutSignal(opts.signal, timeoutMs);

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body:
        opts.rawBody !== undefined
          ? opts.rawBody
          : opts.body !== undefined
            ? JSON.stringify(opts.body)
            : undefined,
      signal,
    });

    if (!res.ok) throw await httpError(res, { skipProjectRecovery: opts.projectId !== undefined });

    return (await res.json()) as T;
  } catch (err) {
    throw toRequestError(err, opts.signal, timeoutMs);
  } finally {
    release();
  }
}

export interface BlobDownload {
  blob: Blob;
  /** From Content-Disposition, or null when the header is absent/unparseable. */
  filename: string | null;
}

/**
 * Binary-response sibling of `request()`. The xlsx / PDF endpoints can't use
 * the JSON helper, but they still need the same timeout + abort handling —
 * these were the four naked fetches that could hang indefinitely.
 *
 * Critically, the BODY IS READ HERE, not by the callers. An earlier version
 * returned the Response and let each caller `await res.blob()` afterwards,
 * which reopened the exact hang this helper exists to close: the timeout only
 * covered the headers. On the `AbortSignal.timeout` path a slow body did abort,
 * but the DOMException escaped `toRequestError` and surfaced raw instead of as
 * our prose timeout; on the fallback path `release()` had already cleared the
 * timer when headers arrived, leaving the body read completely unbounded. An
 * xlsx render streams for real, so the body is the slow part — it is precisely
 * what needs to be inside the window.
 */
async function fetchBlob(
  path: string,
  /**
   * Headers are narrowed to a plain record on purpose. The active-project
   * header is merged in below, and a spread of a `Headers` instance yields an
   * empty object — which would have silently dropped the caller's
   * Authorization. Making the wide type unrepresentable is cheaper than
   * normalizing at runtime.
   */
  init: Omit<RequestInit, 'headers'> & { headers?: Record<string, string> },
  opts: ApiCallOptions = {},
): Promise<BlobDownload> {
  const timeoutMs = opts.timeoutMs ?? DOWNLOAD_TIMEOUT_MS;
  const { signal, release } = withTimeoutSignal(opts.signal, timeoutMs);
  try {
    // The four blob callers each pass their own `authorization` header; the
    // active-project header is merged in here so they can't drift out of sync
    // with request(). Caller headers win on collision.
    const res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: { ...activeProjectHeader(), ...init.headers },
      signal,
    });
    if (!res.ok) throw await httpError(res);
    const filename = parseFilenameFromContentDisposition(
      res.headers.get('content-disposition'),
    );
    return { blob: await res.blob(), filename };
  } catch (err) {
    throw toRequestError(err, opts.signal, timeoutMs);
  } finally {
    release();
  }
}

export const api = {
  requestMagicLink: (email: string) =>
    request<RequestMagicLinkResponse>('/api/auth/request-magic-link', {
      method: 'POST',
      body: { email },
    }),

  verifyMagicLink: (token: string) =>
    request<VerifyMagicLinkResponse>('/api/auth/verify', {
      method: 'POST',
      body: { token },
    }),

  /**
   * Which sign-in doors are open. Unauthenticated; the login page reads it on
   * mount. NOTE the snake_case field — it is `magic_link_enabled`, and reading
   * a camelCase spelling here yields `undefined`, which every truthiness test
   * would quietly turn into "enabled". That is the one bug this endpoint can
   * have, and it survives cutover invisibly.
   */
  authConfig: (opts: ApiCallOptions = {}) =>
    request<AuthConfigResponse>('/api/auth/config', { ...opts }),

  login: (email: string, password: string) =>
    request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: { email, password } satisfies LoginRequest,
      // scrypt is deliberate server-side work and the request may also be
      // waiting on a cold Worker; the default 20s ceiling covers both. Naming
      // it here is the point: a slow login is EXPECTED, not a bug.
      timeoutMs: REQUEST_TIMEOUT_MS,
    }),

  /** Always resolves for a well-formed email — the server never says whether the account exists. */
  forgotPassword: (email: string) =>
    request<ForgotPasswordResponse>('/api/auth/forgot-password', {
      method: 'POST',
      body: { email } satisfies ForgotPasswordRequest,
    }),

  resetPassword: (token: string, password: string) =>
    request<SetPasswordResponse>('/api/auth/reset-password', {
      method: 'POST',
      body: { token, password } satisfies SetPasswordRequest,
    }),

  acceptInvite: (token: string, password: string) =>
    request<SetPasswordResponse>('/api/auth/accept-invite', {
      method: 'POST',
      body: { token, password } satisfies SetPasswordRequest,
    }),

  me: (token: string) => request<MeResponse>('/api/auth/me', { token }),

  logout: (token: string) => request<{ ok: true }>('/api/auth/logout', { method: 'POST', token }),

  // ----- Settings (read-only Phase 2 Slice 2) -----
  settings: {
    project: (token: string, opts: ApiCallOptions = {}) =>
      request<SettingsProject>('/api/settings/project', { token, ...opts }),
    employees: (token: string, opts: ApiCallOptions = {}) =>
      request<SettingsEmployee[]>('/api/settings/employees', { token, ...opts }),
    equipment: (token: string, opts: ApiCallOptions = {}) =>
      request<SettingsEquipment[]>('/api/settings/equipment', { token, ...opts }),
    materials: (token: string, opts: ApiCallOptions = {}) =>
      request<SettingsMaterial[]>('/api/settings/materials', { token, ...opts }),
    pcos: (token: string, opts: ApiCallOptions = {}) =>
      request<SettingsPco[]>('/api/settings/pcos', { token, ...opts }),
    /**
     * The same list for ANOTHER project this session belongs to — the
     * cross-project transfer dialog's destination picker. The server still
     * proves membership on that id; this only asks.
     */
    pcosForProject: (token: string, projectId: string, opts: ApiCallOptions = {}) =>
      request<SettingsPco[]>('/api/settings/pcos', { token, projectId, ...opts }),
    activityCodes: (token: string, opts: ApiCallOptions = {}) =>
      request<SettingsActivityCode[]>('/api/settings/activity-codes', { token, ...opts }),
    unknowns: (token: string, opts: ApiCallOptions = {}) =>
      request<SettingsUnknownsResponse>('/api/settings/unknowns', { token, ...opts }),
    /**
     * The crew-slot decisions this job remembers — what the portal repeats on
     * every later report from that foreman. Nothing writes here from Settings;
     * decisions are made on a reassign in the PDF Inbox and only forgotten
     * here.
     */
    foremanPlaceholders: (token: string, opts: ApiCallOptions = {}) =>
      request<SettingsForemanPlaceholder[]>('/api/settings/foreman-placeholders', {
        token,
        ...opts,
      }),
    /**
     * The same employee or asset id across every job the caller is on, for
     * the Compare rates table. The one settings read that spans projects —
     * membership is the fence, and the server applies it.
     */
    rateComparison: (token: string, kind: RateComparisonKind, opts: ApiCallOptions = {}) =>
      request<RateComparisonResponse>(`/api/settings/rate-comparison?kind=${kind}`, {
        token,
        ...opts,
      }),
    /** Forget one. Forward-only — lines it already filled keep their person. */
    forgetForemanPlaceholder: (token: string, id: string) =>
      request<{ ok: true }>(`/api/settings/foreman-placeholders/${id}`, {
        method: 'DELETE',
        token,
      }),

    // ----- Settings mutations -----
    patchProject: (token: string, body: ProjectPatchRequest) =>
      request<ProjectPatchResponse>('/api/settings/project', { method: 'PATCH', token, body }),

    /**
     * Clear the amber "these equipment rates came from another job" reminder
     * the New Project wizard stamps on a copied project. One direction only —
     * nothing in the UI can set the note, so this endpoint takes no body.
     */
    dismissEquipmentReviewNote: (token: string) =>
      request<DismissEquipmentReviewNoteResponse>(
        '/api/settings/project/equipment-review-note',
        { method: 'PATCH', token },
      ),

    createEmployee: (token: string, body: EmployeeWriteRequest) =>
      request<SettingsEmployee>('/api/settings/employees', { method: 'POST', token, body }),
    applyEmployeeDefaults: (token: string, body: ApplyEmployeeDefaultsRequest) =>
      request<ApplyEmployeeDefaultsResponse>('/api/settings/employees/apply-defaults', {
        method: 'POST',
        token,
        body,
      }),
    /**
     * "Apply to saved blocks" on the Employees / Equipment tabs. `confirm:
     * false` is the preview (counts only, writes nothing); `confirm: true`
     * re-prices saved-but-unsent rows at the current roster rates.
     */
    applyRatesToSaved: (token: string, body: ApplyRatesToSavedRequest) =>
      request<ApplyRatesToSavedResponse>('/api/settings/apply-rates-to-saved', {
        method: 'POST',
        token,
        body,
      }),
    updateEmployee: (token: string, id: string, body: EmployeeWriteRequest) =>
      request<SettingsEmployee>(`/api/settings/employees/${id}`, { method: 'PUT', token, body }),
    deleteEmployee: (token: string, id: string) =>
      request<{ ok: true }>(`/api/settings/employees/${id}`, { method: 'DELETE', token }),

    /**
     * Surgical classification edit — used by the inline Class dropdown in
     * the inbox labor table. Updates the employees row directly (no PCO/week
     * scope, no lock guard). For rollup-table edits use api.rollups.setLaborClass
     * which carries the locked-snapshot guard.
     */
    setEmployeeClassification: (token: string, id: string, classification: string) =>
      request<SettingsEmployee>(`/api/settings/employees/${id}/classification`, {
        method: 'PATCH',
        token,
        body: { classification },
      }),

    createEquipment: (token: string, body: EquipmentWriteRequest) =>
      request<SettingsEquipment>('/api/settings/equipment', { method: 'POST', token, body }),
    updateEquipment: (token: string, id: string, body: EquipmentWriteRequest) =>
      request<SettingsEquipment>(`/api/settings/equipment/${id}`, { method: 'PUT', token, body }),
    deleteEquipment: (token: string, id: string) =>
      request<{ ok: true }>(`/api/settings/equipment/${id}`, { method: 'DELETE', token }),

    createMaterial: (token: string, body: MaterialWriteRequest) =>
      request<SettingsMaterial>('/api/settings/materials', { method: 'POST', token, body }),
    updateMaterial: (token: string, id: string, body: MaterialWriteRequest) =>
      request<SettingsMaterial>(`/api/settings/materials/${id}`, { method: 'PUT', token, body }),
    deleteMaterial: (token: string, id: string) =>
      request<{ ok: true }>(`/api/settings/materials/${id}`, { method: 'DELETE', token }),

    createPco: (token: string, body: PcoWriteRequest) =>
      request<SettingsPco>('/api/settings/pcos', { method: 'POST', token, body }),
    updatePco: (token: string, id: string, body: PcoWriteRequest) =>
      request<SettingsPco>(`/api/settings/pcos/${id}`, { method: 'PUT', token, body }),
    deletePco: (token: string, id: string) =>
      request<{ ok: true }>(`/api/settings/pcos/${id}`, { method: 'DELETE', token }),

    /**
     * Reassign an activity code's default PCO mapping (or unmap with null).
     * Forward-only: updates the mapping + the suggested PCO on still-pending
     * blocks; never moves saved/submitted work. Always succeeds (no 409),
     * returning the count of pending blocks whose suggestion was re-pointed.
     */
    assignActivityCode: (token: string, id: string, mappedPcoId: string | null) =>
      request<CodeRemapSuccessResponse>(`/api/settings/activity-codes/${id}`, {
        method: 'PUT',
        token,
        body: { mapped_pco_id: mappedPcoId },
      }),

    /**
     * Put an activity code on the IGNORE list (or take it off). Ignored codes
     * are contract / lump-sum work that never bills here — their blocks skip
     * the PDF Inbox entirely.
     *
     * Retroactive and reversible: ignoring converts the code's still-pending
     * blocks to 'ignored', un-ignoring restores them to 'pending'. Saved
     * (billed) blocks are never touched. `409 code_is_mapped` when the code is
     * routed to a PCO — unmap it first.
     */
    setActivityCodeIgnored: (token: string, id: string, ignored: boolean) => {
      const body: ActivityCodeIgnoreRequest = { ignored };
      return request<ActivityCodeIgnoreResponse>(
        `/api/settings/activity-codes/${id}/ignore`,
        { method: 'PATCH', token, body },
      );
    },

    /**
     * Light status-only PATCH. Backs the PCO command center's Mark Complete
     * (status -> 'paid') and Reopen (status -> 'active') actions.
     */
    patchPcoStatus: (token: string, id: string, status: PcoStatus) => {
      const body: PcoStatusPatchRequest = { status };
      return request<SettingsPco>(`/api/settings/pcos/${id}/status`, {
        method: 'PATCH',
        token,
        body,
      });
    },
  },

  // ----- Projects (admin: the New Project wizard) -----
  projects: {
    /**
     * Every job the admin may copy a new project from, with each one's active
     * roster counts — the wizard's Copy from list. Admin-gated, like the
     * create it feeds.
     */
    copySources: (token: string, opts: ApiCallOptions = {}) =>
      request<CopySourcesResponse>('/api/projects/copy-sources', { token, ...opts }),

    /**
     * Create a project, its system From-address, and copies of whichever
     * rosters the wizard selected. `source_project_id` names the job to copy
     * from; omitted, the server copies from the ACTIVE project, which is what
     * the wizard sends whenever the pick is the job the admin is standing in.
     *
     * 409 `project_number_exists` on a job-number clash — the wizard surfaces
     * that one inline on the project-number field rather than as a form error.
     * 403 `source_forbidden` / 404 `source_not_found` are about the PICK, and
     * deliberately not the `project_forbidden` whose stale-selection recovery
     * would reload the wizard away.
     */
    create: (token: string, body: CreateProjectRequest) =>
      request<CreateProjectResponse>('/api/projects/new', { method: 'POST', token, body }),
  },

  // ----- User administration (admin: Settings -> Users) -----
  //
  // Account-level, not project-level — which is why it is `/api/users` and not
  // `/api/settings/users`. Every call 403s `forbidden_not_admin` for a non-admin;
  // the tab hides itself as a courtesy, the server is the gate.
  //
  // There is NO delete. "Remove access" is `deactivate`, and it is reversible.
  users: {
    list: (token: string, opts: ApiCallOptions = {}) =>
      request<AdminUsersListResponse>('/api/users', { token, ...opts }),

    /**
     * Create the account and email an invite. The new user chooses their own
     * password through that link, so no credential ever transits any channel.
     * They are granted membership on the CALLER'S active project — an account
     * with no membership gets a 403 the app has no screen for.
     *
     * `invite_sent: false` in the response means the row exists but the email
     * failed; Re-invite is the fix. 409 `email_exists` on a duplicate address.
     */
    create: (token: string, body: CreateUserRequest) =>
      request<CreateUserResponse>('/api/users', { method: 'POST', token, body }),

    /** Re-send. Kills the previous unconsumed invite — newest email wins. */
    invite: (token: string, id: string) =>
      request<InviteUserResponse>(`/api/users/${id}/invite`, { method: 'POST', token }),

    /** 409 `cannot_change_own_role` on a self role change; name is unguarded. */
    patch: (token: string, id: string, body: PatchUserRequest) =>
      request<AdminUserResponse>(`/api/users/${id}`, { method: 'PATCH', token, body }),

    /** Signs them out of every device in the same operation. 409 on self. */
    deactivate: (token: string, id: string) =>
      request<DeactivateUserResponse>(`/api/users/${id}/deactivate`, { method: 'POST', token }),

    reactivate: (token: string, id: string) =>
      request<AdminUserResponse>(`/api/users/${id}/reactivate`, { method: 'POST', token }),

    /** Clears the sign-in backoff AND the email-scoped throttle windows. */
    unlock: (token: string, id: string) =>
      request<UnlockUserResponse>(`/api/users/${id}/unlock`, { method: 'POST', token }),

    revokeSessions: (token: string, id: string) =>
      request<RevokeUserSessionsResponse>(`/api/users/${id}/revoke-sessions`, {
        method: 'POST',
        token,
      }),
  },

  // ----- Materials Queue -----
  materials: {
    list: (
      token: string,
      status: MaterialQueueStatusFilter = 'pending',
      opts: ApiCallOptions = {},
    ) =>
      request<MaterialQueueListResponse>(
        `/api/materials/queue${status === 'pending' ? '' : `?status=${status}`}`,
        { token, ...opts },
      ),

    confirm: (token: string, id: string, body: MaterialQueueConfirmRequest = {}) =>
      request<MaterialQueueItemResponse>(`/api/materials/queue/${id}/confirm`, {
        method: 'POST',
        token,
        body,
      }),

    skip: (token: string, id: string) =>
      request<MaterialQueueItemResponse>(`/api/materials/queue/${id}/skip`, {
        method: 'POST',
        token,
      }),

    reset: (token: string, id: string) =>
      request<MaterialQueueItemResponse>(`/api/materials/queue/${id}/reset`, {
        method: 'POST',
        token,
      }),
  },

  // ----- Weekly Rollups -----
  rollups: {
    list: (token: string, opts: ApiCallOptions = {}) =>
      request<RollupListResponse>('/api/rollups/weekly', { token, ...opts }),

    detail: (
      token: string,
      pcoId: string,
      weekEndingDate: string,
      opts: ApiCallOptions = {},
    ) =>
      request<RollupDetail>(`/api/rollups/weekly/${pcoId}/${weekEndingDate}`, {
        token,
        ...opts,
      }),

    /** ADR 0003 source-blocks list — the blocks making up this rollup. */
    blocks: (token: string, pcoId: string, weekEndingDate: string) =>
      request<RollupBlocksResponse>(
        `/api/rollups/weekly/${pcoId}/${weekEndingDate}/blocks`,
        { token },
      ),

    /** ADR 0004 manual-add — labor / equipment / material lines with no PDF. */
    manualAddLabor: (token: string, pcoId: string, week: string, body: ManualLaborAddRequest) =>
      request<ManualAddResponse>(`/api/rollups/weekly/${pcoId}/${week}/labor/manual-add`, {
        method: 'POST',
        token,
        body,
      }),
    manualAddEquipment: (
      token: string,
      pcoId: string,
      week: string,
      body: ManualEquipmentAddRequest,
    ) =>
      request<ManualAddResponse>(`/api/rollups/weekly/${pcoId}/${week}/equipment/manual-add`, {
        method: 'POST',
        token,
        body,
      }),
    manualAddMaterial: (
      token: string,
      pcoId: string,
      week: string,
      body: ManualMaterialAddRequest,
    ) =>
      request<ManualAddResponse>(`/api/rollups/weekly/${pcoId}/${week}/material/manual-add`, {
        method: 'POST',
        token,
        body,
      }),

    /** Aggregated breakdown across every week of this PCO's activity. */
    cumulative: (token: string, pcoId: string, opts: ApiCallOptions = {}) =>
      request<PcoCumulativeResponse>(`/api/rollups/pco/${pcoId}/cumulative`, {
        token,
        ...opts,
      }),

    /**
     * Regenerated copies of this PCO's contributing dailies — the Sources tab's
     * "Generated PDF" column. Sidecar to `cumulative` so the money read stays
     * one query; only the Sources tab pays for this one.
     */
    generatedDocuments: (token: string, pcoId: string, opts: ApiCallOptions = {}) =>
      request<PcoGeneratedDocumentsResponse>(
        `/api/rollups/pco/${pcoId}/generated-documents`,
        { token, ...opts },
      ),

    /**
     * Download the rollup as xlsx. Returns a Blob the caller can save to disk
     * (typical pattern: createObjectURL + anchor click). Bypasses the JSON
     * `request()` helper because the response body is binary.
     */
    /**
     * Approve & Send. Renders xlsx, packages PDFs, sends to Hoffman via
     * Resend, locks the rollup. Pass {dry_run: true} in the body to preview
     * without sending — useful for an "are you sure?" confirmation step.
     */
    submit: (
      token: string,
      pcoId: string,
      weekEndingDate: string,
      body: SubmitRollupRequest = {},
    ) =>
      request<SubmitRollupResponse>(
        `/api/rollups/weekly/${pcoId}/${weekEndingDate}/submit`,
        { method: 'POST', token, body },
      ),

    /**
     * Preview-composer render (client decision 2026-07-22). Read-only — returns
     * the exact subject/body/recipients/attachments an Approve & Send would
     * produce, reusing the server's renderCoverLetter so the preview can't drift
     * from the sent email. Pass the live `custom_message` to see it inserted.
     */
    preview: (
      token: string,
      pcoId: string,
      weekEndingDate: string,
      body: { custom_message?: string } = {},
    ) =>
      request<RollupPreviewResponse>(
        `/api/rollups/weekly/${pcoId}/${weekEndingDate}/preview`,
        { method: 'POST', token, body },
      ),

    /**
     * Flip a submitted rollup back to in_progress. Server recomputes from
     * current truth (pulls in any late PDFs) and clears the stale flag.
     * Prior `submissions` rows are preserved — they're the audit record of
     * what was sent. The next submit creates a "REVISED:" submission.
     */
    reopen: (
      token: string,
      pcoId: string,
      weekEndingDate: string,
      body: ReopenRollupRequest = {},
    ) =>
      request<ReopenRollupResponse>(
        `/api/rollups/weekly/${pcoId}/${weekEndingDate}/reopen`,
        { method: 'POST', token, body },
      ),

    /**
     * Finalize a REOPENED week with no email — the revision becomes the
     * version of record and the GC keeps the copy he already has. Refused on
     * a week that is not open (`409 not_in_progress`) or has never been sent
     * (`409 never_sent`), which is what keeps a first send a real send. Writes
     * no send record: the week's "Send history" stays a list of deliveries.
     */
    closeWithoutSend: (
      token: string,
      pcoId: string,
      weekEndingDate: string,
      body: CloseWithoutSendRequest = {},
    ) =>
      request<CloseWithoutSendResponse>(
        `/api/rollups/weekly/${pcoId}/${weekEndingDate}/close-without-send`,
        { method: 'POST', token, body },
      ),

    /**
     * Mark a sent (submitted) rollup as approved by Hoffman. Manual relabel —
     * totals are untouched (the frozen snapshot is what was approved). Moves
     * it from the Pending bucket to Approved.
     */
    markApproved: (token: string, pcoId: string, weekEndingDate: string) =>
      request<ApproveRollupResponse>(
        `/api/rollups/weekly/${pcoId}/${weekEndingDate}/approve`,
        { method: 'POST', token },
      ),

    /**
     * The per-day rows behind the Labor / Equipment lines of a week. The line
     * tables show weekly aggregates; this is what the edit form picks a DAY
     * from, and `row_id` is what the edit endpoints take.
     */
    lineRows: (
      token: string,
      pcoId: string,
      weekEndingDate: string,
      opts: ApiCallOptions = {},
    ) =>
      request<RollupLineRowsResponse>(
        `/api/rollups/weekly/${pcoId}/${weekEndingDate}/line-rows`,
        { token, ...opts },
      ),

    /**
     * Day-explicit labor line edit. `row_id` names ONE day's row; `hours` and
     * `rate_cents` are that day's absolute values (never a weekly total, never
     * a delta). `apply_rate_to_all_days` extends the RATE — and only the rate —
     * to every day of the same line. Rejected when the rollup is locked.
     *
     * Routed to the hours or rate endpoint by which field is present; both
     * accept the same body and share one server-side core.
     */
    editLaborLine: (
      token: string,
      pcoId: string,
      weekEndingDate: string,
      body: RollupLineEditRequest,
    ) =>
      request<RollupLineEditResponse>(
        `/api/rollups/weekly/${pcoId}/${weekEndingDate}/labor/${
          body.hours === undefined ? 'rate' : 'hours'
        }`,
        { method: 'POST', token, body },
      ),

    /** Same shape as editLaborLine, against the equipment rows. */
    editEquipmentLine: (
      token: string,
      pcoId: string,
      weekEndingDate: string,
      body: RollupLineEditRequest,
    ) =>
      request<RollupLineEditResponse>(
        `/api/rollups/weekly/${pcoId}/${weekEndingDate}/equipment/${
          body.hours === undefined ? 'rate' : 'hours'
        }`,
        { method: 'POST', token, body },
      ),

    /**
     * Inline classification edit. classification is per-employee
     * (employees.classification), so this write is project-wide — every
     * rollup showing this employee reflects the new value after refetch.
     * No totals recompute (display-only field).
     */
    setLaborClass: (
      token: string,
      pcoId: string,
      weekEndingDate: string,
      employeeId: string,
      classification: string,
    ) =>
      request<{ ok: true; no_change?: true }>(
        `/api/rollups/weekly/${pcoId}/${weekEndingDate}/labor/class`,
        {
          method: 'POST',
          token,
          body: { employee_id: employeeId, classification },
        },
      ),

    /**
     * Send history for one (pco, week). Returns most-recent first. Legacy
     * addressing — resolves the week's FIRST rollup, so it is ambiguous on a
     * split week; prefer `submissionsByRollup` wherever a rollup id is in hand.
     */
    submissions: (
      token: string,
      pcoId: string,
      weekEndingDate: string,
      opts: ApiCallOptions = {},
    ) =>
      request<SubmissionsListResponse>(
        `/api/rollups/weekly/${pcoId}/${weekEndingDate}/submissions`,
        { token, ...opts },
      ),

    /**
     * Send history for one EXACT rollup — split-week safe, and what the week
     * page uses (it already holds `detail.rollup_id`). Each item carries the
     * send's own `documents` capture, which is what lets the page prove a
     * daily went out as the foreman's untouched original.
     */
    submissionsByRollup: (token: string, rollupId: string, opts: ApiCallOptions = {}) =>
      request<SubmissionsListResponse>(`/api/rollups/by-rollup/${rollupId}/submissions`, {
        token,
        ...opts,
      }),

    /**
     * Download the archived xlsx for a specific historical submission.
     * Bypasses the JSON request helper because the response body is binary.
     */
    downloadSubmissionXlsx: async (
      token: string,
      submissionId: string,
      opts: ApiCallOptions = {},
    ): Promise<Blob> => {
      const { blob } = await fetchBlob(
        `/api/rollups/submissions/${submissionId}/xlsx`,
        { headers: { authorization: `Bearer ${token}` } },
        opts,
      );
      return blob;
    },

    /**
     * Returns the xlsx blob + the server's preferred filename (parsed from
     * the Content-Disposition header). Server derives the filename via the
     * shared deriveRollupXlsxFilename helper so it matches Joey's existing
     * "PCO 004.6 - Weekly Rollup WE 3.21.26.xlsx" convention.
     */
    downloadXlsx: async (
      token: string,
      pcoId: string,
      weekEndingDate: string,
      opts: ApiCallOptions = {},
    ): Promise<{ blob: Blob; filename: string | null }> => {
      return fetchBlob(
        `/api/rollups/weekly/${pcoId}/${weekEndingDate}/xlsx`,
        {
          method: 'POST',
          headers: { authorization: `Bearer ${token}` },
        },
        opts,
      );
    },

    // ----- Canonical addressing (by rollup id) — split-week safe -----
    //
    // Mirror the legacy (pco, week) methods above but target one exact rollup.
    // A split week has two rollups per (pco, week); these let the UI send /
    // reopen / approve / edit / download the addressed one instead of the
    // default. Non-split behavior is identical (the id is the sole rollup's).

    detailById: (token: string, rollupId: string, opts: ApiCallOptions = {}) =>
      request<RollupDetail>(`/api/rollups/by-rollup/${rollupId}`, { token, ...opts }),

    /** Source-blocks list for one exact rollup (its pin-aware membership). */
    blocksById: (token: string, rollupId: string) =>
      request<RollupBlocksResponse>(`/api/rollups/by-rollup/${rollupId}/blocks`, { token }),

    /** Manual-add into one exact rollup — in a split week the new row is
     *  pinned to it instead of sweeping to the default. */
    manualAddLaborById: (token: string, rollupId: string, body: ManualLaborAddRequest) =>
      request<ManualAddResponse>(`/api/rollups/by-rollup/${rollupId}/labor/manual-add`, {
        method: 'POST',
        token,
        body,
      }),
    manualAddEquipmentById: (token: string, rollupId: string, body: ManualEquipmentAddRequest) =>
      request<ManualAddResponse>(`/api/rollups/by-rollup/${rollupId}/equipment/manual-add`, {
        method: 'POST',
        token,
        body,
      }),
    manualAddMaterialById: (token: string, rollupId: string, body: ManualMaterialAddRequest) =>
      request<ManualAddResponse>(`/api/rollups/by-rollup/${rollupId}/material/manual-add`, {
        method: 'POST',
        token,
        body,
      }),

    // ----- Subcontractor lines (rollup-level lump lines; canonical only) -----

    /** Add an outside-sub vendor-invoice lump line to an in_progress rollup. */
    addSubcontractor: (token: string, rollupId: string, body: SubcontractorLineAddRequest) =>
      request<SubcontractorLineResponse>(`/api/rollups/by-rollup/${rollupId}/subcontractors`, {
        method: 'POST',
        token,
        body,
      }),

    /** Edit a subcontractor line (description / amount / invoice date). */
    updateSubcontractor: (
      token: string,
      rollupId: string,
      lineId: string,
      body: SubcontractorLinePatchRequest,
    ) =>
      request<SubcontractorLineResponse>(
        `/api/rollups/by-rollup/${rollupId}/subcontractors/${lineId}`,
        { method: 'PATCH', token, body },
      ),

    /** Remove a subcontractor line. */
    removeSubcontractor: (token: string, rollupId: string, lineId: string) =>
      request<{ ok: true }>(`/api/rollups/by-rollup/${rollupId}/subcontractors/${lineId}`, {
        method: 'DELETE',
        token,
      }),

    // ----- Vendor invoice PDF on a subcontractor line (2026-07-29) -----
    // A line IS one vendor invoice, so it holds at most one PDF and a
    // re-upload replaces it. Approve & Send attaches every line's invoice.

    /** Upload/replace the line's invoice. Raw PDF bytes, not multipart. */
    uploadSubcontractorInvoice: (token: string, rollupId: string, lineId: string, file: File) =>
      request<SubcontractorLineResponse>(
        `/api/rollups/by-rollup/${rollupId}/subcontractors/${lineId}/invoice`,
        { method: 'PUT', token, rawBody: file, contentType: 'application/pdf' },
      ),

    /** Remove the line's invoice (clears the column + deletes the object). */
    removeSubcontractorInvoice: (token: string, rollupId: string, lineId: string) =>
      request<SubcontractorLineResponse>(
        `/api/rollups/by-rollup/${rollupId}/subcontractors/${lineId}/invoice`,
        { method: 'DELETE', token },
      ),

    /** Short-lived presigned URL for viewing. Works on locked rollups too. */
    subcontractorInvoiceUrl: (token: string, rollupId: string, lineId: string) =>
      request<SignedPdfUrlResponse>(
        `/api/rollups/by-rollup/${rollupId}/subcontractors/${lineId}/invoice/url`,
        { token },
      ),

    submitById: (token: string, rollupId: string, body: SubmitRollupRequest = {}) =>
      request<SubmitRollupResponse>(`/api/rollups/by-rollup/${rollupId}/submit`, {
        method: 'POST',
        token,
        body,
      }),

    /** Canonical (split-week-safe) preview render — see rollups.preview. */
    previewById: (token: string, rollupId: string, body: { custom_message?: string } = {}) =>
      request<RollupPreviewResponse>(`/api/rollups/by-rollup/${rollupId}/preview`, {
        method: 'POST',
        token,
        body,
      }),

    /**
     * Every address this project has actually emailed, most-recently-used
     * first — the send composer's type-to-add suggestions. The whole book
     * comes back in one call and the composer filters it client-side; there is
     * no server-side search.
     *
     * SUGGESTIONS ONLY. Nothing here is pre-selected for a send and nothing
     * here is a permission — an address still has to ride on the send request
     * to reach anyone.
     */
    sendContacts: (token: string, opts: ApiCallOptions = {}) =>
      request<SendContactsResponse>('/api/rollups/send-contacts', { token, ...opts }),

    reopenById: (token: string, rollupId: string, body: ReopenRollupRequest = {}) =>
      request<ReopenRollupResponse>(`/api/rollups/by-rollup/${rollupId}/reopen`, {
        method: 'POST',
        token,
        body,
      }),

    /**
     * Correct an imported (backfill) week's frozen total. The ONE mutation an
     * imported rollup accepts — no reopen, no recompute, total + reason only.
     */
    editImportedTotal: (token: string, rollupId: string, body: EditImportedTotalRequest) =>
      request<EditImportedTotalResponse>(
        `/api/rollups/by-rollup/${rollupId}/imported-total`,
        { method: 'PATCH', token, body },
      ),

    /** Canonical (split-week safe) address for close-without-send. */
    closeWithoutSendById: (
      token: string,
      rollupId: string,
      body: CloseWithoutSendRequest = {},
    ) =>
      request<CloseWithoutSendResponse>(
        `/api/rollups/by-rollup/${rollupId}/close-without-send`,
        { method: 'POST', token, body },
      ),

    markApprovedById: (token: string, rollupId: string) =>
      request<ApproveRollupResponse>(`/api/rollups/by-rollup/${rollupId}/approve`, {
        method: 'POST',
        token,
      }),

    /** Re-home an unsent week into another open submission (or a fresh next one). */
    moveSubmission: (token: string, rollupId: string, body: MoveRollupSubmissionRequest) =>
      request<MoveRollupSubmissionResponse>(
        `/api/rollups/by-rollup/${rollupId}/move-submission`,
        { method: 'POST', token, body },
      ),

    /** Per-day rows behind one exact rollup's lines (split-week safe). */
    lineRowsById: (token: string, rollupId: string, opts: ApiCallOptions = {}) =>
      request<RollupLineRowsResponse>(`/api/rollups/by-rollup/${rollupId}/line-rows`, {
        token,
        ...opts,
      }),

    editLaborLineById: (token: string, rollupId: string, body: RollupLineEditRequest) =>
      request<RollupLineEditResponse>(
        `/api/rollups/by-rollup/${rollupId}/labor/${body.hours === undefined ? 'rate' : 'hours'}`,
        { method: 'POST', token, body },
      ),

    editEquipmentLineById: (token: string, rollupId: string, body: RollupLineEditRequest) =>
      request<RollupLineEditResponse>(
        `/api/rollups/by-rollup/${rollupId}/equipment/${body.hours === undefined ? 'rate' : 'hours'}`,
        { method: 'POST', token, body },
      ),

    downloadXlsxById: async (
      token: string,
      rollupId: string,
      opts: ApiCallOptions = {},
    ): Promise<{ blob: Blob; filename: string | null }> => {
      return fetchBlob(
        `/api/rollups/by-rollup/${rollupId}/xlsx`,
        {
          method: 'POST',
          headers: { authorization: `Bearer ${token}` },
        },
        opts,
      );
    },
  },

  // ----- Submissions (PCO → submission → weekly rollup) -----
  submissions: {
    list: (token: string, pcoId: string, opts: ApiCallOptions = {}) =>
      request<PcoSubmissionsResponse>(`/api/pcos/${pcoId}/submissions`, { token, ...opts }),
    /** Project-wide submissions grouped by PCO — the PCO Log's expand prefetch. */
    listAll: (token: string, opts: ApiCallOptions = {}) =>
      request<ProjectSubmissionsResponse>('/api/submissions', { token, ...opts }),
    closeAndNext: (token: string, pcoId: string, body: CloseAndStartNextRequest) =>
      request<CloseAndStartNextResponse>(`/api/pcos/${pcoId}/submissions/close-and-next`, {
        method: 'POST',
        token,
        body,
      }),
    openAlongside: (token: string, pcoId: string, body: OpenSubmissionRequest) =>
      request<SubmissionResponse>(`/api/pcos/${pcoId}/submissions`, { method: 'POST', token, body }),
    /**
     * Open the PCO's next submission as its new collecting home. Closes
     * nothing and carries nothing — the previous package stays open and ends
     * through Approve & Send. 409 `number_conflict` on an explicit-number
     * clash. Supersedes close-and-next / open-alongside for the Start Next
     * control.
     *
     */
    startNext: (token: string, pcoId: string, body: StartNextSubmissionRequest) =>
      request<SubmissionResponse>(`/api/pcos/${pcoId}/submissions/start-next`, {
        method: 'POST',
        token,
        body,
      }),
    /**
     * Add a pre-portal billing row as a LEDGER-ONLY submission: an amount and
     * a free-text worked range, no weeks, no PDFs, nothing emailed. Never the
     * default target. 409 `number_conflict` on a clash (compared numerically,
     * so '12' hits an existing '012').
     */
    addHistorical: (token: string, pcoId: string, body: AddHistoricalSubmissionRequest) =>
      request<SubmissionResponse>(`/api/pcos/${pcoId}/submissions/historical`, {
        method: 'POST',
        token,
        body,
      }),
    patch: (token: string, submissionId: string, body: SubmissionPatchRequest) =>
      request<SubmissionResponse>(`/api/submissions/${submissionId}`, { method: 'PATCH', token, body }),
    /**
     * Remove a submission that has nothing behind it. Two shapes qualify and
     * the server decides which from the row: a ledger-only historical row (the
     * duplicate-entry fix) and an EMPTY PACKAGE — open, no weeks, not the
     * PCO's collecting default, no imported total. Anything else 409s
     * (`not_ledger_submission` / `not_deletable_submission` /
     * `submission_has_weeks`); this is the only delete of a submission
     * anywhere in the app.
     */
    remove: (token: string, submissionId: string) =>
      request<DeleteSubmissionResponse>(`/api/submissions/${submissionId}`, {
        method: 'DELETE',
        token,
      }),
    /**
     * Close any OPEN submission (clears the default flag if it held it). The
     * counterpart to close-and-next for a package that has been demoted and
     * just needs to end — 409 `unsent_weeks_exist` while it still holds
     * in_progress weeks.
     */
    close: (token: string, submissionId: string) =>
      request<SubmissionResponse>(`/api/submissions/${submissionId}/close`, {
        method: 'POST',
        token,
      }),
  },

  // ----- Intake Health -----
  intake: {
    health: (token: string, days = 7, opts: ApiCallOptions = {}) =>
      request<IntakeHealthResponse>(`/api/intake/health?days=${days}`, { token, ...opts }),

    /**
     * Self-serve retry of a failed PDF (recovers from a transient parser /
     * upstream blip). Returns the re-run outcome — 'parsed' means it's now in
     * the inbox; 'failed' means it errored again.
     */
    reparse: (token: string, id: string, opts: ApiCallOptions = {}) =>
      request<ReparsePdfResponse>(`/api/intake/pdfs/${id}/reparse`, {
        method: 'POST',
        token,
        // Runs the whole parse inline, model call included — the 20s default
        // would kill a perfectly healthy retry partway through.
        timeoutMs: REPARSE_TIMEOUT_MS,
        ...opts,
      }),
  },

  // ----- Foreman coverage (missing-daily alarm) -----
  coverage: {
    /**
     * Every foreman with a daily in the lookback, quietest first, with his
     * last daily and how many site-days he has been silent.
     */
    list: (token: string, opts: ApiCallOptions = {}) =>
      request<ForemanCoverageResponse>('/api/coverage', { token, ...opts }),

    /**
     * Account for a silence. `paused_until` omitted/null is the open-ended
     * pause ("no longer on this job"); a date is the vacation case and lifts
     * on its own the day after.
     */
    pause: (token: string, body: PauseForemanCoverageRequest, opts: ApiCallOptions = {}) =>
      request<ForemanCoverageMutationResponse>('/api/coverage/pause', {
        method: 'POST',
        token,
        body,
        ...opts,
      }),

    /** Put him back on watch. Clears the pause AND its note. */
    resume: (token: string, body: ResumeForemanCoverageRequest, opts: ApiCallOptions = {}) =>
      request<ForemanCoverageMutationResponse>('/api/coverage/resume', {
        method: 'POST',
        token,
        body,
        ...opts,
      }),
  },

  // ----- PDF preview -----
  pdfs: {
    signedUrl: (token: string, id: string) =>
      request<SignedPdfUrlResponse>(`/api/pdfs/${id}/signed-url`, { token }),

    /**
     * A regenerated daily by `generated_documents.id`. Returns the ORIGINAL's
     * filename — the send attaches the regenerated copy under that exact name,
     * so anything else here would name a file Hoffman never received.
     */
    generatedSignedUrl: (token: string, id: string) =>
      request<SignedPdfUrlResponse>(`/api/pdfs/generated/${id}/signed-url`, { token }),
  },

  // ----- PCO-scoped reads + funding tranches (ADR 0002) -----
  pcos: {
    /** Aggregated attention surface for the NeedsAttention panel. */
    attention: (token: string, pcoId: string, opts: ApiCallOptions = {}) =>
      request<PcoAttentionResponse>(`/api/pcos/${pcoId}/attention`, { token, ...opts }),

    /** Clear the attention flag that ranks a PCO at the top of the side rail's
     *  Recent PCOs (ADR 0004 workflow nudge). Fired when the PM opens the PCO
     *  command center. */
    markReviewed: (token: string, pcoId: string) =>
      request<{ ok: true }>(`/api/pcos/${pcoId}/mark-reviewed`, { method: 'POST', token }),

    /** Project-wide 6-up totals strip data (Original / Approved CO / Revised
     *  / Submitted to date / Pending CO / Pending Contract). */
    projectTotals: (token: string, opts: ApiCallOptions = {}) =>
      request<ProjectTotals>('/api/pcos/projects/current/totals', { token, ...opts }),

    /** List funding tranches for one PCO. */
    listTranches: (token: string, pcoId: string, opts: ApiCallOptions = {}) =>
      request<FundingTranche[]>(`/api/pcos/${pcoId}/tranches`, { token, ...opts }),

    /** Create a funding tranche. */
    createTranche: (token: string, pcoId: string, body: FundingTrancheWriteRequest) =>
      request<FundingTranche>(`/api/pcos/${pcoId}/tranches`, {
        method: 'POST',
        token,
        body,
      }),

    /** Patch tranche fields (partial). */
    updateTranche: (
      token: string,
      trancheId: string,
      body: Partial<FundingTrancheWriteRequest>,
    ) =>
      request<FundingTranche>(`/api/pcos/tranches/${trancheId}`, {
        method: 'PATCH',
        token,
        body,
      }),

    /** Delete a tranche. Cascade does not remove rollups (rollups attribute
     *  to the parent PCO, not to a tranche — ADR 0002). */
    deleteTranche: (token: string, trancheId: string) =>
      request<{ ok: true }>(`/api/pcos/tranches/${trancheId}`, {
        method: 'DELETE',
        token,
      }),

    /** Download the server-rendered PCO Log xlsx as a Blob. The hook
     *  triggers the browser download from a button click handler. */
    downloadPcoLogXlsx: async (
      token: string,
      opts: ApiCallOptions = {},
    ): Promise<{ blob: Blob; filename: string | null }> => {
      // Was hand-rolling its own filename regex and error shape; both are now
      // the shared ones. `parseFilenameFromContentDisposition` is a strict
      // superset of the old pattern (it also handles RFC 5987 `filename*`),
      // and a non-JSON error body still reaches the caller as `payload.message`.
      return fetchBlob(
        `/api/pcos/projects/current/pco-log.xlsx`,
        { headers: { Authorization: `Bearer ${token}` } },
        opts,
      );
    },
  },

  // ----- Dashboard -----
  dashboard: {
    /** Active-PCOs control panel — one row per active PCO. */
    activePcos: (token: string, opts: ApiCallOptions = {}) =>
      request<ActivePcosResponse>('/api/dashboard/active-pcos', { token, ...opts }),
  },

  // ----- PDF Import Inbox -----
  inbox: {
    /**
     * List of PDFs with at least one block_status='pending' block.
     * `filters.pcoId` and `filters.foreman` COMPOSE (AND) into the same
     * request — pass either, both, or neither for the unfiltered list.
     * `pcoId: 'unassigned'` is a sentinel (pending blocks with no effective
     * PCO), sent verbatim; `foreman` is an exact `daily_pdfs.foreman_name`
     * match.
     *
     * `includeSkipped` additionally asks for the decided PDFs whose skipped
     * blocks have no other surface. They come back in their own
     * `skipped_pdfs` array — never mixed into `pdfs`.
     */
    list: (
      token: string,
      filters?: { pcoId?: string | null; foreman?: string | null; includeSkipped?: boolean },
      opts: ApiCallOptions = {},
    ) => {
      const params = new URLSearchParams();
      if (filters?.pcoId) params.set('pco_id', filters.pcoId);
      if (filters?.foreman) params.set('foreman', filters.foreman);
      if (filters?.includeSkipped) params.set('include_skipped', '1');
      const qs = params.toString();
      return request<InboxListResponse>(`/api/inbox${qs ? `?${qs}` : ''}`, { token, ...opts });
    },

    /** Full per-block detail for one PDF. */
    pdfDetail: (token: string, dailyPdfId: string, opts: ApiCallOptions = {}) =>
      request<InboxPdfDetailResponse>(`/api/inbox/pdfs/${dailyPdfId}`, { token, ...opts }),

    /**
     * Dismiss the independent re-read's disagreement on one daily — "checked
     * against the PDF, the parse is right". Flips the daily to verified and
     * records who said so inside verify_json, clearing the list's amber tag and
     * the send composer's acknowledgment. 409 `not_flagged` when there is
     * nothing to dismiss (already cleared, or re-verified underneath).
     */
    acknowledgeVerify: (token: string, dailyPdfId: string) =>
      request<InboxVerifyAckResponse>(`/api/inbox/pdfs/${dailyPdfId}/verify-ack`, {
        method: 'POST',
        token,
      }),

    /** Commit one block — writes confirmed_* values + sets block_status='saved'. */
    saveBlock: (token: string, dailyEntryId: string, body: InboxBlockSaveRequest) =>
      request<InboxBlockResponse>(`/api/inbox/blocks/${dailyEntryId}/save`, {
        method: 'POST',
        token,
        body,
      }),

    /**
     * "Replace older block" — the near-duplicate banner's one-click
     * resolution. Same payload as saveBlock, plus which SAVED block this one
     * supersedes. See InboxBlockReplaceRequest/Response
     * (packages/shared/src/api.ts) for the server's guarantees, including the
     * `partial` / `remaining_action` half-done case.
     */
    replaceBlock: (
      token: string,
      dailyEntryId: string,
      body: InboxBlockSaveRequest & InboxBlockReplaceRequest,
    ) =>
      request<InboxBlockReplaceResponse>(`/api/inbox/blocks/${dailyEntryId}/replace`, {
        method: 'POST',
        token,
        body,
      }),

    /** Mark a block 'skipped' — confirmed_* stays null. */
    skipBlock: (token: string, dailyEntryId: string) =>
      request<InboxBlockResponse>(`/api/inbox/blocks/${dailyEntryId}/skip`, {
        method: 'POST',
        token,
      }),

    /**
     * Flip a 'saved' or 'skipped' block back to 'pending' so Joey can re-edit.
     * confirmed_* values stay intact (last edits become the new defaults).
     * Server recomputes rollups — the block stops contributing until re-saved.
     */
    reopenBlock: (token: string, dailyEntryId: string) =>
      request<InboxBlockResponse>(`/api/inbox/blocks/${dailyEntryId}/reopen`, {
        method: 'POST',
        token,
      }),
    addLabor: (token: string, dailyEntryId: string, body: InboxLaborAddRequest) =>
      request<InboxBlockResponse>(`/api/inbox/blocks/${dailyEntryId}/labor/add`, {
        method: 'POST',
        token,
        body,
      }),
    addEquipment: (token: string, dailyEntryId: string, body: InboxEquipmentAddRequest) =>
      request<InboxBlockResponse>(`/api/inbox/blocks/${dailyEntryId}/equipment/add`, {
        method: 'POST',
        token,
        body,
      }),
    addMaterial: (token: string, dailyEntryId: string, body: InboxMaterialAddRequest) =>
      request<InboxBlockResponse>(`/api/inbox/blocks/${dailyEntryId}/material/add`, {
        method: 'POST',
        token,
        body,
      }),
    removeManualLine: (
      token: string,
      dailyEntryId: string,
      kind: 'labor' | 'equipment' | 'material',
      rowId: string,
    ) =>
      request<InboxBlockResponse>(
        `/api/inbox/blocks/${dailyEntryId}/${kind}/${rowId}`,
        { method: 'DELETE', token },
      ),
    /**
     * Point a labor line at an existing roster employee (null clears back to
     * the parsed identity). Pending blocks only; the parsed *_raw text stays
     * as the audit of what the foreman submitted.
     */
    reassignLabor: (
      token: string,
      dailyEntryId: string,
      rowId: string,
      body: InboxLaborReassignRequest,
    ) =>
      request<InboxLaborReassignResponse>(
        `/api/inbox/blocks/${dailyEntryId}/labor/${rowId}/reassign`,
        { method: 'POST', token, body },
      ),
    /** Equipment twin of reassignLabor. */
    reassignEquipment: (
      token: string,
      dailyEntryId: string,
      rowId: string,
      body: InboxEquipmentReassignRequest,
    ) =>
      request<InboxBlockResponse>(
        `/api/inbox/blocks/${dailyEntryId}/equipment/${rowId}/reassign`,
        { method: 'POST', token, body },
      ),
  },

  // ----- Feedback notes (trial-week feedback feature) -----
  feedback: {
    /** Joey-side write. */
    create: (token: string, body: CreateFeedbackNoteRequest) =>
      request<CreateFeedbackNoteResponse>('/api/feedback/notes', { method: 'POST', token, body }),
    /** Caller's own notes (the bubble's "your previous notes" list). */
    mine: (token: string) =>
      request<FeedbackNotesListResponse>('/api/feedback/notes/mine', { token }),
    /** Admin: all notes, optional status filter. */
    list: (token: string, status?: FeedbackNoteStatus) =>
      request<FeedbackNotesListResponse>(
        `/api/feedback/notes${status ? `?status=${status}` : ''}`,
        { token },
      ),
    /** Admin: update status / response. */
    patch: (token: string, id: string, body: FeedbackNotePatchRequest) =>
      request<FeedbackNoteResponse>(`/api/feedback/notes/${id}`, { method: 'PATCH', token, body }),
  },

  // ----- Block reassignment (ADR 0003) -----
  blocks: {
    /** Read-only single-block detail (labor/equipment/materials) for View Block. */
    detail: (token: string, dailyEntryId: string) =>
      request<InboxBlockResponse>(`/api/blocks/${dailyEntryId}`, { token }),

    move: (token: string, dailyEntryId: string, body: MoveBlockRequest) =>
      request<MoveBlockResponse>(`/api/blocks/${dailyEntryId}/move`, {
        method: 'POST',
        token,
        body,
      }),

    /** Carve-out mover — pin one or more saved blocks into a submission. */
    assignSubmission: (token: string, submissionId: string, blockIds: string[]) =>
      request<AssignBlocksResponse>('/api/blocks/assign-submission', {
        method: 'POST',
        token,
        body: { submission_id: submissionId, block_ids: blockIds },
      }),

    /**
     * Cross-project transfer — move a PENDING or SKIPPED block onto another
     * job's PCO, re-pricing every person and machine against that job's roster.
     * Lands PENDING in the target's inbox for a normal review + save.
     */
    transferProject: (
      token: string,
      dailyEntryId: string,
      body: TransferBlockProjectRequest,
    ) =>
      request<TransferBlockProjectResponse>(`/api/blocks/${dailyEntryId}/transfer-project`, {
        method: 'POST',
        token,
        body,
      }),
  },

  // ----- Reconciliation -----
  reconciliation: {
    list: (
      token: string,
      status: VarianceStatusFilter = 'open',
      opts: ApiCallOptions = {},
    ) =>
      request<VarianceListResponse>(
        `/api/reconciliation/variances${status === 'all' ? '?status=all' : `?status=${status}`}`,
        { token, ...opts },
      ),

    create: (token: string, body: VarianceCreateRequest) =>
      request<VarianceItemResponse>('/api/reconciliation/variances', {
        method: 'POST',
        token,
        body,
      }),

    patch: (token: string, id: string, body: VariancePatchRequest) =>
      request<VarianceItemResponse>(`/api/reconciliation/variances/${id}`, {
        method: 'PATCH',
        token,
        body,
      }),

    remove: (token: string, id: string) =>
      request<{ ok: true }>(`/api/reconciliation/variances/${id}`, {
        method: 'DELETE',
        token,
      }),
  },
};

/**
 * Pull a quoted or unquoted filename out of a Content-Disposition header.
 * Handles `attachment; filename="..."`, `attachment; filename=...`, and
 * RFC 5987 `filename*=UTF-8''...`. Returns null when no filename is
 * present.
 */
function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // RFC 5987 (UTF-8) takes precedence when both are present.
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* fall through */
    }
  }
  const plain = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(header);
  return plain?.[1]?.trim() ?? plain?.[2]?.trim() ?? null;
}
